import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, FileText, Loader2, ShieldCheck, Upload, X } from 'lucide-react';
import { formatEuro } from '../../utils/currency';
import { scanInvoicePdf, type InvoiceScan } from '../../lib/invoicePdf';
import type { FulfilledInvoice } from '../../services/invoiceRequestService';

const round2 = (n: number) => Math.round(n * 100) / 100;

type FieldKey = 'invoice_number' | 'issued_date' | 'due_date' | 'purchase_order' | 'net_amount' | 'total_amount';

interface Props {
  /** "Fatura 1/2 · 750,00 €" */
  label: string;
  requestedAmount: number;
  clientVat: string | null;
  defaultPurchaseOrder: string;
  onClose: () => void;
  onConfirm: (invoice: FulfilledInvoice) => Promise<void>;
}

/**
 * Reads the invoice PDF on this computer (nothing is uploaded or kept) and
 * lets the person check every field before it goes into the campaign.
 */
export function InvoiceScanModal({ label, requestedAmount, clientVat, defaultPurchaseOrder, onClose, onConfirm }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState<InvoiceScan | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    invoice_number: '',
    issued_date: '',
    due_date: '',
    purchase_order: defaultPurchaseOrder,
    net_amount: '',
    total_amount: '',
  });
  const [fromPdf, setFromPdf] = useState<Set<FieldKey>>(new Set());

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setFileName(file.name);
    setScanning(true);
    setReadFailed(false);
    try {
      const result = await scanInvoicePdf(file, clientVat);
      setScan(result);
      const read: Partial<Record<FieldKey, string>> = {
        invoice_number: result.invoiceNumber ?? undefined,
        issued_date: result.issueDate ?? undefined,
        due_date: result.dueDate ?? undefined,
        purchase_order: result.purchaseOrder ?? undefined,
        net_amount: result.netAmount !== null ? String(result.netAmount) : undefined,
        total_amount: result.totalAmount !== null ? String(result.totalAmount) : undefined,
      };
      const found = (Object.keys(read) as FieldKey[]).filter(k => read[k]);
      setFromPdf(new Set(found));
      setForm(prev => ({ ...prev, ...Object.fromEntries(found.map(k => [k, read[k]])) }));
      if (found.length === 0) setReadFailed(true);
    } catch {
      setReadFailed(true);
    } finally {
      setScanning(false);
    }
  }

  const set = (key: FieldKey, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setFromPdf(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const net = form.net_amount ? parseFloat(form.net_amount) : null;
  const amountDiffers = net !== null && Math.abs(net - requestedAmount) > 0.01;
  const canSave = form.invoice_number.trim() !== '' && form.issued_date !== '';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!canSave) return;
    setSaving(true);
    try {
      await onConfirm({
        invoice_number: form.invoice_number.trim(),
        issued_date: form.issued_date,
        due_date: form.due_date,
        purchase_order: form.purchase_order.trim(),
        net_amount: net !== null ? round2(net) : null,
        total_amount: form.total_amount ? round2(parseFloat(form.total_amount)) : null,
      });
    } finally {
      setSaving(false);
    }
  }

  const field = (key: FieldKey, text: string, type: 'text' | 'date' | 'number', required = false) => (
    <div>
      <label className="cm-label flex items-center gap-1.5" htmlFor={`scan-${key}`}>
        {text}{required && <span className="text-red-500">*</span>}
        {fromPdf.has(key) && <span className="cm-chip cm-chip-success !py-0 text-[10px]">lido do PDF</span>}
      </label>
      <input
        id={`scan-${key}`}
        type={type}
        step={type === 'number' ? '0.01' : undefined}
        min={type === 'number' ? '0' : undefined}
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        required={required}
        className={`${type === 'date' ? 'cm-date' : 'cm-input'} ${type === 'number' ? 'cm-numeric' : ''} mt-1 ${fromPdf.has(key) ? 'bg-emerald-50/60' : ''}`}
      />
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 bg-brand-ink/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4" role="dialog" aria-modal="true" aria-label="Carregar fatura">
      <form onSubmit={handleSubmit} className="bg-white rounded-card shadow-float max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="mo-display text-lg text-brand-ink">Carregar fatura</h2>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fechar">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
            className="w-full rounded-xl border-2 border-dashed border-gray-300 hover:border-brand-ink px-4 py-5 text-center transition-colors"
          >
            {scanning ? (
              <span className="inline-flex items-center gap-2 text-sm text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> A ler a fatura…</span>
            ) : fileName ? (
              <span className="inline-flex items-center gap-2 text-sm text-brand-ink"><FileText className="w-4 h-4" /> {fileName} <span className="text-gray-400">· trocar</span></span>
            ) : (
              <span className="inline-flex flex-col items-center gap-1 text-sm text-gray-600">
                <Upload className="w-5 h-5" />
                Escolhe ou arrasta o PDF da fatura
              </span>
            )}
          </button>
          <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
          <p className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
            O PDF é lido neste computador e não fica guardado. Confirma os campos antes de registar.
          </p>

          {readFailed && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Não foi possível ler dados deste PDF (pode ser uma imagem digitalizada). Preenche os campos à mão.
            </p>
          )}
          {scan && !readFailed && (
            <p className="flex items-center gap-1.5 text-xs text-emerald-700">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {fromPdf.size} de 6 campos lidos{scan.documentNumber ? ` · ${scan.documentNumber}` : ''}
            </p>
          )}
          {scan?.vatNumberFound === false && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              O NIF do cliente ({clientVat}) não aparece nesta fatura. Confirma que é a fatura certa.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field('invoice_number', 'Nº da fatura', 'text', true)}
            {field('purchase_order', 'Ordem de compra', 'text')}
            {field('issued_date', 'Data de emissão', 'date', true)}
            {field('due_date', 'Vencimento', 'date')}
            {field('net_amount', 'Valor sem IVA', 'number')}
            {field('total_amount', 'Total com IVA', 'number')}
          </div>

          {amountDiffers && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              O valor sem IVA ({formatEuro(net!)}) é diferente do pedido ({formatEuro(requestedAmount)}). Fica registado o valor da fatura.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="cm-btn cm-btn-ghost">Cancelar</button>
          <button type="submit" disabled={!canSave || saving || scanning} className="cm-btn cm-btn-primary">
            {saving ? 'A registar…' : 'Registar fatura'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
